import { AccessTimeOutlined, WorkOutlineOutlined } from "@mui/icons-material";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { type Dispatch, type FC, type SetStateAction, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { SettingsSection } from "../../../../components/UIToolkit/molecules/SettingsSection";
import { CONFIG_TABLE_CONTAINER_SX, CONFIG_TABLE_HEADER_CELL_SX } from "../../../../components/UIToolkit/Tables/ConfigTable/configTableSx";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { INK } from "../../../../theme/tokens";
import type { NurseRole } from "../../../../utils/models";
import { DepartmentJobsConfig } from "./DepartmentJobsConfig";
import { SIDE_LAID_GROUND_SX } from "./ratesConsts";
import { ShiftLengthsTableRow } from "./ShiftLengthsTableRow";
import { collectPartialHourOptions } from "./shiftTimes";
import type { GeneralFormData } from "./types";

export const ShiftsAndRolesTabContent: FC<{
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
	isNewDept: boolean;
}> = ({ formData, setFormData, isNewDept }) => {
	const { t } = useTranslation();

	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const jobMode = isNewDept || jobManagementEnabled;

	// Role mode only — in job mode the Jobs grid collects its own options from the same maps.
	const hourOptions = useMemo(
		() => collectPartialHourOptions(formData.jobShiftTimes, formData.partialBlockShifts),
		[formData.jobShiftTimes, formData.partialBlockShifts],
	);

	return (
		<Box component="div" sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
			{/* Jobs — one grid: which jobs are active, their department group, PPD inclusion, shift times. */}
			<Box sx={SIDE_LAID_GROUND_SX}>
				<SettingsSection
					headerPlacement="side"
					icon={<WorkOutlineOutlined />}
					title={t("department.config.shifts.section.jobs")}
					description={
						<Trans
							i18nKey="department.config.shifts.section.jobs.description"
							values={{ department: formData.displayName }}
							components={{ b: <Box component="span" sx={{ fontWeight: 600, color: INK.SECONDARY }} /> }}
						/>
					}
					layout="stack"
					// Six columns plus a shift-times editor panel: at 980px the panel's From/To pair wrapped
					// and the two shift columns truncated. Same treatment as the Integrations tab's wide tables.
					contentMaxWidth="none"
					dataTestId="dept-config-shifts-jobs-header"
				>
					<DepartmentJobsConfig formData={formData} setFormData={setFormData} isNewDept={isNewDept} jobMode={jobMode} />
				</SettingsSection>

				{/*
				 * Shift lengths — ROLE MODE ONLY. In job mode these values are keyed by job and are edited
				 * inside the Jobs grid above (one table, DAT-804); here they are keyed by `NurseRole`, which
				 * a jobs-keyed grid cannot represent, so the original table stays exactly as it was. The
				 * mode-independent `default` bucket moves with them: it lives in this table in role mode, and
				 * in the Jobs section's fallback card in job mode.
				 */}
				{!jobMode && (
					<SettingsSection
						headerPlacement="side"
						icon={<AccessTimeOutlined />}
						title={t("department.config.shifts.section.shiftLengths")}
						description={t("department.config.shifts.section.shiftLengths.description")}
						layout="stack"
						contentMaxWidth={980}
						dataTestId="dept-config-shifts-lengths-header"
					>
						<TableContainer component={Paper} sx={CONFIG_TABLE_CONTAINER_SX}>
							<Table sx={{ width: "100%" }} aria-label={t("department.config.shifts.section.shiftLengths")}>
								<TableHead>
									<TableRow>
										<TableCell align="left" sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: 120 }]}>
											{t("department.config.shifts.col.role")}
										</TableCell>
										<TableCell align="left" sx={CONFIG_TABLE_HEADER_CELL_SX}>
											{t("department.config.shifts.col.partialShiftLengths")}
										</TableCell>
										<TableCell align="left" sx={CONFIG_TABLE_HEADER_CELL_SX}>
											{t("department.config.shifts.col.fullShiftTimes")}
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{Object.entries(formData.partialBlockShifts)
										.filter(([role]) => role !== "default")
										.map(([role, hours]) => (
											<ShiftLengthsTableRow
												key={role}
												rowKey={role}
												label={role}
												mode="role"
												hours={hours}
												fullShiftTimes={formData.fullShiftTimes[role as NurseRole] || []}
												hourOptions={hourOptions}
												setFormData={setFormData}
											/>
										))}
									<ShiftLengthsTableRow
										key="default"
										rowKey="default"
										label="default"
										mode="role"
										hours={formData.partialBlockShifts.default || []}
										fullShiftTimes={formData.fullShiftTimes.default || []}
										hourOptions={hourOptions}
										setFormData={setFormData}
									/>
								</TableBody>
							</Table>
						</TableContainer>
					</SettingsSection>
				)}
			</Box>
		</Box>
	);
};
